import { Injectable } from '@angular/core';
import {
  FormGroupDirective,
  NgForm,
  FormControl,
  FormGroup,
  NgControl,
} from '@angular/forms';

import { Subject } from 'rxjs';

export type Constructor<T> = new (...args: any[]) => T;

/** Error state matcher that matches when a control is invalid and dirty. */
@Injectable()
export class ShowOnDirtyErrorStateMatcher implements ErrorStateMatcher {
  isError(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(
      control &&
      control.invalid &&
      (control.dirty || (form && form.submitted))
    );
  }
}

/** Provider that defines how form controls behave with regards to displaying error messages. */
@Injectable({ providedIn: 'root' })
export class ErrorStateMatcher {
  isError(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(
      control &&
      control.invalid &&
      (control.touched || (form && form.submitted))
    );
  }
}

export interface CanUpdateErrorState {
  readonly stateChanges: Subject<void>;
  isError: boolean;
  errorStateMatcher: ErrorStateMatcher;
  isErrorStateFrozen: boolean;
  updateIsError();
}

export interface HasErrorState {
  _parentFormGroup: FormGroupDirective;
  _defaultErrorStateMatcher: ErrorStateMatcher;
  ngControl: NgControl;
}

/**
 * Mixin to augment a directive with updateErrorState method.
 * For component with `errorState` and need to update `errorState`.
 */
export function mixinIsError<T extends Constructor<HasErrorState>>(
  base: T
): Constructor<CanUpdateErrorState> & T {
  return class extends base {
    isError = false;
    isErrorStateFrozen: boolean;

    readonly stateChanges = new Subject<void>();

    errorStateMatcher: ErrorStateMatcher;

    updateIsError() {
      const oldState = this.isError;
      const parent = this._parentFormGroup;
      const matcher = this.errorStateMatcher || this._defaultErrorStateMatcher;
      const control = this.ngControl
        ? (this.ngControl.control as FormControl)
        : null;
      const newState = matcher.isError(control, parent);

      if (newState !== oldState && !this.isErrorStateFrozen) {
        this.isError = newState;
        this.stateChanges.next();
      }
    }

    constructor(...args: any[]) {
      super(...args);
    }
  };
}
